import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, DatePicker, Empty, Row, Select, Space, Spin, Tabs, Typography, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import WarehouseRankingList from './components/WarehouseRankingList';
import MetricScoreCards from './components/MetricScoreCards';
import DspTopList from './components/DspTopList';
import ScoreTrendTable from './components/ScoreTrendTable';
import AttributionForm from './components/AttributionForm';
import ScoringFormulaPanel from './components/ScoringFormulaPanel';
import { LIGHT_COLORS, TREND_DAYS } from './constants';
import type { AttributionRecord, DspScoreItem, MetricScore, TrendPoint, WarehouseScoreItem } from './types';
import { useOperableHubSelectOptions } from '../../../hooks/useOperableHubSelectOptions';
import { WarehouseBusinessType } from '../../../graphql/types/dashboard/SortingDashboardTypes';
import { showError } from '../../../utils/exceptionUtils';
import { dashboardNowInPacific } from '../../../utils/dashboardPacificTime';
import {
    LOAD_DAILY_MEETING_ATTRIBUTIONS,
    QUERY_DAILY_MEETING_DSP_TOP,
    QUERY_DAILY_MEETING_RANKING,
    QUERY_DAILY_MEETING_SCORE_THRESHOLD,
    QUERY_DAILY_MEETING_TREND,
} from '../../../graphql/queries/dashboard/queryDailyMeeting';
import { CREATE_DAILY_MEETING_ATTRIBUTION } from '../../../graphql/mutations/dashboard/createDailyMeetingAttribution';
import { UPDATE_DAILY_MEETING_ATTRIBUTION } from '../../../graphql/mutations/dashboard/updateDailyMeetingAttribution';
import { DELETE_DAILY_MEETING_ATTRIBUTION } from '../../../graphql/mutations/dashboard/deleteDailyMeetingAttribution';
import type {
    CreateDailyMeetingAttributionResponse,
    CreateDailyMeetingAttributionVariables,
    DailyMeetingAttributionType,
    DailyMeetingDspScoreType,
    DailyMeetingMetricScoreType,
    DailyMeetingTrendPointType,
    DailyMeetingWarehouseScoreType,
    DeleteDailyMeetingAttributionResponse,
    DeleteDailyMeetingAttributionVariables,
    LoadDailyMeetingAttributionsResponse,
    LoadDailyMeetingAttributionsVariables,
    QueryDailyMeetingDspTopResponse,
    QueryDailyMeetingDspTopVariables,
    QueryDailyMeetingRankingResponse,
    QueryDailyMeetingRankingVariables,
    QueryDailyMeetingScoreThresholdResponse,
    QueryDailyMeetingTrendResponse,
    QueryDailyMeetingTrendVariables,
    UpdateDailyMeetingAttributionResponse,
    UpdateDailyMeetingAttributionVariables,
} from '../../../graphql/types/dashboard/DailyMeetingTypes';

/** 数据日期：默认美西 T-2，可选范围 [T-(1+TREND_DAYS), T-2]；比"规模数据"（默认/上限 T-1）整体早一天，且窗口宽度与趋势保留天数对齐 */
const DEFAULT_STAT_DATE_OFFSET_DAYS = 2;

const { Title, Text } = Typography;

/** 后端未挂到 warehouse-service 时 hubName/warehouseName 可能回落 null，UI 兜底 */
const nameFallback = (name: string | null | undefined, id: number | null | undefined) =>
    name && name.length > 0 ? name : id != null ? String(id) : '-';

const mapMetric = (m: DailyMeetingMetricScoreType): MetricScore => ({
    metric: m.metric as MetricScore['metric'],
    actual: m.actual,
    target: m.target,
    gap: m.gap,
    sev: m.sev,
    consecutiveDays: m.consecutiveDays,
    burst: m.burst,
    contribution: m.contribution,
});

const mapWarehouse = (row: DailyMeetingWarehouseScoreType): WarehouseScoreItem => ({
    hubId: row.hubId ?? 0,
    hubName: nameFallback(row.hubName, row.hubId),
    warehouseId: row.warehouseId,
    warehouseName: nameFallback(row.warehouseName, row.warehouseId),
    statDate: row.statDate,
    actualArrivals: row.actualArrivals,
    totalScore: row.totalScore,
    light: row.light,
    metrics: row.metrics.map(mapMetric),
});

const mapDsp = (row: DailyMeetingDspScoreType): DspScoreItem => ({
    dspId: row.dspId,
    dspName: nameFallback(row.dspName, row.dspId),
    actualArrivals: row.actualArrivals,
    totalScore: row.totalScore,
    metrics: row.metrics.map(mapMetric),
});

const mapTrend = (row: DailyMeetingTrendPointType): TrendPoint => ({
    statDate: row.statDate,
    totalScore: row.totalScore,
    light: row.light,
    actualArrivals: row.actualArrivals,
    metrics: (row.metrics ?? []).map(mapMetric),
});

const mapAttribution = (row: DailyMeetingAttributionType): AttributionRecord => ({
    id: row.id,
    warehouseId: row.warehouseId,
    statDate: row.statDate,
    reason: row.reason ?? undefined,
    notes: row.notes ?? undefined,
    expectedRecoveryDate: row.expectedRecoveryDate ?? undefined,
    claimStatus: row.claimStatus,
    claimerId: row.claimerId ?? undefined,
    claimerName: row.claimerName ?? undefined,
    creatorId: row.creatorId ?? undefined,
    creatorName: row.creatorName ?? undefined,
    updatedById: row.updatedById ?? undefined,
    updatedByName: row.updatedByName ?? undefined,
    createdAt: row.createdAt ?? undefined,
    updatedAt: row.updatedAt ?? undefined,
});

const DailyMeetingPage: React.FC = () => {
    const { t } = useTranslation();
    const I18N = 'dashboard.dailyMeeting';

    const [statDate, setStatDate] = useState<string>(
        () => dashboardNowInPacific().subtract(DEFAULT_STAT_DATE_OFFSET_DAYS, 'day').format('YYYY-MM-DD'),
    );
    const [hubId, setHubId] = useState<number>(0);
    const [warehouseFilterId, setWarehouseFilterId] = useState<number>(0);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | undefined>(undefined);

    const { hubOptions } = useOperableHubSelectOptions({
        showAllOption: true,
        allOptionLabel: t(`${I18N}.hubAll`) || '全部',
        filterBusinessTypes: [WarehouseBusinessType.LAST_MILE],
    });

    const thresholdResp = useQuery<QueryDailyMeetingScoreThresholdResponse>(
        QUERY_DAILY_MEETING_SCORE_THRESHOLD,
        {
            fetchPolicy: 'cache-first',
            onError: (e) => showError(e),
        },
    );
    const scoreThreshold = thresholdResp.data?.queryDailyMeetingScoreThreshold;

    const rankingResp = useQuery<QueryDailyMeetingRankingResponse, QueryDailyMeetingRankingVariables>(
        QUERY_DAILY_MEETING_RANKING,
        {
            variables: { statDate, hubId },
            fetchPolicy: 'cache-and-network',
            onError: (e) => showError(e),
        },
    );
    const ranking: WarehouseScoreItem[] = useMemo(
        () => (rankingResp.data?.queryDailyMeetingRanking ?? []).map(mapWarehouse),
        [rankingResp.data],
    );

    /** whs 下拉候选：从当前 ranking 去重后按名称首字母排序，"全部"固定顶格（ranking 已按 hubId 过滤，hub 联动天然成立） */
    const warehouseOptions = useMemo(() => {
        const seen = new Set<number>();
        const items: { value: number; label: string }[] = [];
        ranking.forEach((r) => {
            if (seen.has(r.warehouseId)) return;
            seen.add(r.warehouseId);
            items.push({ value: r.warehouseId, label: r.warehouseName });
        });
        items.sort((a, b) => a.label.localeCompare(b.label));
        return [{ value: 0, label: t(`${I18N}.hubAll`) || '全部' }, ...items];
    }, [ranking, t]);

    useEffect(() => {
        // 换 hub / 换日期后，原选中 whs 可能已不在候选中，回退到"全部"
        if (warehouseFilterId === 0) return;
        const stillAvailable = ranking.some((r) => r.warehouseId === warehouseFilterId);
        if (!stillAvailable) setWarehouseFilterId(0);
    }, [ranking, warehouseFilterId]);

    const filteredRanking = useMemo(
        () => (warehouseFilterId === 0 ? ranking : ranking.filter((r) => r.warehouseId === warehouseFilterId)),
        [ranking, warehouseFilterId],
    );

    useEffect(() => {
        // 换日期/换 hub/换 whs 后原选中仓可能已不在列表里，回退到榜首
        const stillVisible = filteredRanking.some((item) => item.warehouseId === selectedWarehouseId);
        if (!stillVisible) {
            setSelectedWarehouseId(filteredRanking[0]?.warehouseId);
        }
    }, [filteredRanking, selectedWarehouseId]);

    const selected = filteredRanking.find((item) => item.warehouseId === selectedWarehouseId);

    const dspResp = useQuery<QueryDailyMeetingDspTopResponse, QueryDailyMeetingDspTopVariables>(
        QUERY_DAILY_MEETING_DSP_TOP,
        {
            variables: selected ? { statDate, warehouseId: selected.warehouseId } : ({} as QueryDailyMeetingDspTopVariables),
            skip: !selected,
            fetchPolicy: 'cache-and-network',
            onError: (e) => showError(e),
        },
    );
    const dspTop: DspScoreItem[] = useMemo(
        () => (dspResp.data?.queryDailyMeetingDspTop ?? []).map(mapDsp),
        [dspResp.data],
    );

    const trendResp = useQuery<QueryDailyMeetingTrendResponse, QueryDailyMeetingTrendVariables>(
        QUERY_DAILY_MEETING_TREND,
        {
            variables: selected ? { warehouseId: selected.warehouseId, endDate: statDate } : ({} as QueryDailyMeetingTrendVariables),
            skip: !selected,
            fetchPolicy: 'cache-and-network',
            onError: (e) => showError(e),
        },
    );
    const trend: TrendPoint[] = useMemo(
        () => (trendResp.data?.queryDailyMeetingTrend ?? []).map(mapTrend),
        [trendResp.data],
    );

    const warehouseIdsForAttr = useMemo(() => ranking.map((r) => r.warehouseId), [ranking]);
    const attributionsResp = useQuery<LoadDailyMeetingAttributionsResponse, LoadDailyMeetingAttributionsVariables>(
        LOAD_DAILY_MEETING_ATTRIBUTIONS,
        {
            variables: { statDate, warehouseIds: warehouseIdsForAttr },
            skip: warehouseIdsForAttr.length === 0,
            fetchPolicy: 'cache-and-network',
            onError: (e) => showError(e),
        },
    );
    /** 索引：warehouseId → 该仓所有归因（按 createdAt ASC；后端已排序） */
    const attributionsByWarehouse: Record<number, AttributionRecord[]> = useMemo(() => {
        const list = attributionsResp.data?.loadDailyMeetingAttributions ?? [];
        return list.reduce((acc, row) => {
            const rec = mapAttribution(row);
            (acc[rec.warehouseId] ??= []).push(rec);
            return acc;
        }, {} as Record<number, AttributionRecord[]>);
    }, [attributionsResp.data]);

    const [createAttribution, createState] = useMutation<CreateDailyMeetingAttributionResponse, CreateDailyMeetingAttributionVariables>(
        CREATE_DAILY_MEETING_ATTRIBUTION,
    );
    const [updateAttribution, updateState] = useMutation<UpdateDailyMeetingAttributionResponse, UpdateDailyMeetingAttributionVariables>(
        UPDATE_DAILY_MEETING_ATTRIBUTION,
    );
    const [deleteAttribution, deleteState] = useMutation<DeleteDailyMeetingAttributionResponse, DeleteDailyMeetingAttributionVariables>(
        DELETE_DAILY_MEETING_ATTRIBUTION,
    );
    const attributionMutating = createState.loading || updateState.loading || deleteState.loading;

    const lightCounts = useMemo(() => ({
        RED: filteredRanking.filter((item) => item.light === 'RED').length,
        YELLOW: filteredRanking.filter((item) => item.light === 'YELLOW').length,
        GREEN: filteredRanking.filter((item) => item.light === 'GREEN').length,
    }), [filteredRanking]);

    /** 「已归因」= 存在任一归因行（多人补录场景下按存在性判定，覆盖 EXISTS 语义） */
    const attributedWarehouseIds = useMemo(
        () => new Set(
            Object.entries(attributionsByWarehouse)
                .filter(([, rows]) => rows.length > 0)
                .map(([wid]) => Number(wid)),
        ),
        [attributionsByWarehouse],
    );

    const handleCreate = async (payload: {
        warehouseId: number;
        statDate: string;
        reason: string;
        notes: string;
        expectedRecoveryDate: string;
    }) => {
        await createAttribution({
            variables: {
                input: {
                    warehouseId: payload.warehouseId,
                    statDate: payload.statDate,
                    reason: payload.reason,
                    notes: payload.notes,
                    expectedRecoveryDate: payload.expectedRecoveryDate,
                },
            },
        });
        message.success(t(`${I18N}.attribution.saved`));
        await attributionsResp.refetch();
    };

    const handleUpdate = async (payload: { id: number; reason: string; notes: string; expectedRecoveryDate: string }) => {
        await updateAttribution({
            variables: {
                input: {
                    id: payload.id,
                    reason: payload.reason,
                    notes: payload.notes,
                    expectedRecoveryDate: payload.expectedRecoveryDate,
                },
            },
        });
        message.success(t(`${I18N}.attribution.saved`));
        await attributionsResp.refetch();
    };

    const handleDelete = async (id: number) => {
        await deleteAttribution({ variables: { id } });
        message.success(t(`${I18N}.attribution.deleted`));
        await attributionsResp.refetch();
    };

    const handleSelectWarehouse = (item: WarehouseScoreItem) => setSelectedWarehouseId(item.warehouseId);

    const topBarRef = useRef<HTMLDivElement>(null);
    const [topBarHeight, setTopBarHeight] = useState(0);
    useLayoutEffect(() => {
        const el = topBarRef.current;
        if (!el) return;
        const update = () => setTopBarHeight(el.offsetHeight);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div className="p-6 min-h-full flex flex-col">
            <div ref={topBarRef} className="sticky top-0 z-20 bg-white pb-4">
            <Card
                bordered={false}
                style={{ boxShadow: 'none', borderLeft: '1px solid #f0f0f0' }}
                title={
                    <span className="text-xl font-semibold">
                        {t(`${I18N}.pageTitle`)}
                        <span className="text-sm font-normal ml-3">{t(`${I18N}.pageTitleTzNote`)}</span>
                    </span>
                }
            >
                <div className="grid grid-cols-3 gap-4 gap-y-3 max-w-screen-xl">
                    <div className="flex items-center gap-2">
                        <span className="!font-bold text-gray-800 whitespace-nowrap">{t(`${I18N}.statDate`)}</span>
                        <DatePicker
                            className="flex-1 min-w-0"
                            allowClear={false}
                            value={dayjs(statDate)}
                            disabledDate={(current) => {
                                if (!current) return false;
                                const latestStr = dashboardNowInPacific()
                                    .subtract(DEFAULT_STAT_DATE_OFFSET_DAYS, 'day')
                                    .format('YYYY-MM-DD');
                                const earliestStr = dashboardNowInPacific()
                                    .subtract(DEFAULT_STAT_DATE_OFFSET_DAYS + TREND_DAYS - 1, 'day')
                                    .format('YYYY-MM-DD');
                                const pickedStr = current.format('YYYY-MM-DD');
                                return pickedStr < earliestStr || pickedStr > latestStr;
                            }}
                            onChange={(value) => value && setStatDate(value.format('YYYY-MM-DD'))}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="!font-bold text-gray-800 whitespace-nowrap">{t(`${I18N}.hub`)}</span>
                        <Select
                            className="flex-1 min-w-0"
                            placeholder={t(`${I18N}.hubAll`)}
                            value={hubId}
                            onChange={(value: number) => setHubId(value)}
                            options={hubOptions}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="!font-bold text-gray-800 whitespace-nowrap">{t(`${I18N}.warehouse`)}</span>
                        <Select
                            className="flex-1 min-w-0"
                            placeholder={t(`${I18N}.hubAll`)}
                            value={warehouseFilterId}
                            onChange={(value: number) => setWarehouseFilterId(value)}
                            options={warehouseOptions}
                            showSearch
                            optionFilterProp="label"
                        />
                    </div>
                </div>
            </Card>
            </div>

            <Row gutter={16} style={{ flex: 1 }}>
                <Col xs={24} lg={8} style={{ position: 'sticky', top: topBarHeight, alignSelf: 'flex-start', zIndex: 10 }}>
                    <Card
                        title={
                            <Space size="middle">
                                <span>{t(`${I18N}.ranking.title`)}</span>
                                <Space size={12}>
                                    <span className="inline-flex items-center gap-1 text-sm font-normal">
                                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: LIGHT_COLORS.RED }} />
                                        <span style={{ color: LIGHT_COLORS.RED }}>{lightCounts.RED}</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-sm font-normal">
                                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: LIGHT_COLORS.YELLOW }} />
                                        <span style={{ color: LIGHT_COLORS.YELLOW }}>{lightCounts.YELLOW}</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-sm font-normal">
                                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: LIGHT_COLORS.GREEN }} />
                                        <span style={{ color: LIGHT_COLORS.GREEN }}>{lightCounts.GREEN}</span>
                                    </span>
                                </Space>
                            </Space>
                        }
                        styles={{
                            body: {
                                padding: 0,
                                height: `calc(100vh - ${topBarHeight}px - 120px)`,
                                overflowY: 'auto',
                            },
                        }}
                        loading={rankingResp.loading && ranking.length === 0}
                    >
                        <WarehouseRankingList
                            items={filteredRanking}
                            selectedWarehouseId={selectedWarehouseId}
                            attributedWarehouseIds={attributedWarehouseIds}
                            onSelect={handleSelectWarehouse}
                        />
                    </Card>
                </Col>

                <Col xs={24} lg={16}>
                    {!selected ? (
                        <Card><Empty description={t(`${I18N}.detail.empty`)} /></Card>
                    ) : (
                        <Card
                            title={
                                <Space>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: LIGHT_COLORS[selected.light],
                                        }}
                                    />
                                    <Text strong>{selected.warehouseName}</Text>
                                    <Text type="secondary">{selected.hubName}</Text>
                                    <Text type="secondary" className="text-xs">
                                        {t(`${I18N}.ranking.arrivals`, { value: Number(selected.actualArrivals).toLocaleString() })}
                                    </Text>
                                </Space>
                            }
                            extra={
                                <Text strong style={{ color: LIGHT_COLORS[selected.light], fontSize: 20 }}>
                                    {selected.totalScore.toFixed(1)}
                                </Text>
                            }
                        >
                            <Tabs
                                items={[
                                    {
                                        key: 'metrics',
                                        label: t(`${I18N}.tab.metrics`),
                                        children: (
                                            <Space direction="vertical" size="large" className="w-full">
                                                <MetricScoreCards metrics={selected.metrics} />
                                                <div>
                                                    <Title level={5}>{t(`${I18N}.dsp.title`)}</Title>
                                                    <Text type="secondary" className="text-xs">{t(`${I18N}.dsp.subtitle`)}</Text>
                                                    <div className="mt-2">
                                                        <Spin spinning={dspResp.loading && dspTop.length === 0}>
                                                            <DspTopList items={dspTop} />
                                                        </Spin>
                                                    </div>
                                                </div>
                                                <ScoringFormulaPanel />
                                                <AttributionForm
                                                    warehouse={selected}
                                                    records={attributionsByWarehouse[selected.warehouseId] ?? []}
                                                    submitting={attributionMutating}
                                                    onCreate={handleCreate}
                                                    onUpdate={handleUpdate}
                                                    onDelete={handleDelete}
                                                />
                                            </Space>
                                        ),
                                    },
                                    {
                                        key: 'trend',
                                        label: (
                                            <Space size={8}>
                                                <span>{t(`${I18N}.tab.trend`, { days: TREND_DAYS })}</span>
                                                {scoreThreshold && (
                                                    <Text type="secondary" className="text-xs">
                                                        {t(`${I18N}.trend.threshold`, { red: scoreThreshold.red, yellow: scoreThreshold.yellow })}
                                                    </Text>
                                                )}
                                            </Space>
                                        ),
                                        children: scoreThreshold && (
                                            <Spin spinning={trendResp.loading && trend.length === 0}>
                                                <ScoreTrendTable
                                                    points={trend}
                                                    redThreshold={scoreThreshold.red}
                                                    yellowThreshold={scoreThreshold.yellow}
                                                />
                                            </Spin>
                                        ),
                                    },
                                ]}
                            />
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default DailyMeetingPage;
